import React, { useEffect, useState } from "react";
import { Table, Alert } from "react-bootstrap";
import EnrollmentService from "../../../_services/EnrollmentService";
import CollapsibleSection from "./CollapsibleSection";

interface ModulesSectionProps {
  enrollmentId: number;
}

const ModulesSection: React.FC<ModulesSectionProps> = ({ enrollmentId }) => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setError(null);
    EnrollmentService.getModules(enrollmentId)
      .then((res: any) => {
        if (res.data?.success) setData(res.data.data);
        else setError(res.data?.message || "Failed to load modules");
      })
      .catch((err: any) =>
        setError(
          err.response?.data?.message || err.message || "Failed to load modules",
        ),
      )
      .finally(() => setLoading(false));
  }, [enrollmentId]);

  if (loading) {
    return (
      <CollapsibleSection title="Modules">
        <div className="ev-loading"><i className="fa fa-spinner fa-spin" /> Loading modules...</div>
      </CollapsibleSection>
    );
  }

  // Surface a load failure instead of silently hiding the block (a failed
  // fetch must not look identical to "no modules").
  if (error) {
    return (
      <CollapsibleSection title="Modules">
        <Alert variant="danger" style={{ fontSize: 12, marginBottom: 0 }}>
          {error}
        </Alert>
      </CollapsibleSection>
    );
  }

  const active = data?.activeModules || [];
  const deferred = data?.deferredModules || [];

  // Match Java: the Modules block is data-driven — when there are no
  // registered (active) or deferred modules, the block is not shown at all.
  if (active.length === 0 && deferred.length === 0) {
    return null;
  }

  const renderTable = (modules: any[]) => (
    <Table bordered hover size="sm" style={{ fontSize: 12, marginBottom: 0 }}>
      <thead>
        <tr>
          <th>Course</th>
          <th>Code</th>
          <th>Term</th>
          <th style={{ textAlign: "right" }}>Amount</th>
          <th>Start Date</th>
          <th>End Date</th>
          <th>Credits</th>
          <th>Status</th>
          <th>Elective</th>
        </tr>
      </thead>
      <tbody>
        {modules.map((m: any) => (
          <tr key={m.id}>
            <td>{m.courseName}</td>
            <td>{m.courseCode}</td>
            <td>{m.termName}</td>
            <td style={{ textAlign: "right" }}>{m.amount != null ? m.amount.toLocaleString() : "--"}</td>
            <td>{m.startDate || "--"}</td>
            <td>{m.endDate || "--"}</td>
            <td>{m.credits ?? "--"}</td>
            <td>{m.status || "--"}</td>
            <td>{m.electiveName || "--"}</td>
          </tr>
        ))}
      </tbody>
    </Table>
  );

  return (
    <>
      <CollapsibleSection title="Modules" count={active.length}>
        {active.length > 0 ? renderTable(active) : <div className="ev-empty">No active modules</div>}
      </CollapsibleSection>

      {deferred.length > 0 && (
        <CollapsibleSection title="Deferred Modules" count={deferred.length} defaultOpen={false}>
          {renderTable(deferred)}
        </CollapsibleSection>
      )}
    </>
  );
};

export default ModulesSection;
